import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import Table, { RowCell } from 'components/Table';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  CourseTable_delegate,
  CourseTable_delegate$data
} from './__generated__/CourseTable_delegate.graphql';
import moment from 'moment';
import FilterOptions, {
  FilterOptionsState
} from 'components/Table/FilterOptions';
import CourseCompletion from 'sharedComponents/core/CourseCompletion';
import Status from 'sharedComponents/core/Table/Status';
import Button from 'components/Button/Button';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';

const useStyles = createUseStyles((theme: Theme) => ({
  courseName: {
    fontWeight: 700,
    fontSize: '0.9rem',
    whiteSpace: 'normal'
  },
  allowBreak: {
    whiteSpace: 'normal'
  }
}));

type Props = {
  delegate: CourseTable_delegate;
};

function CourseTable({ delegate }: Props) {
  const classes = useStyles();
  const action = useActionMenuDispatch();

  const [courseFilters, setCourseFilters] = React.useState<FilterOptionsState>({
    hideExpired: {
      label: 'Hide expired courses',
      type: 'checkbox',
      checked: true
    }
  });

  // to accomodate for hybrid courses
  const online_courses = delegate?.myCourses ?? [];
  const classroom_courses = delegate?.myClassroomCourses ?? [];

  const filtered_classroom_courses = classroom_courses.filter(
    (c) =>
      !(
        c.status == 'complete' &&
        online_courses.some(
          (c2) =>
            c2.course?.partTwo && c2.course.partTwo.ident === c.course?.ident
        )
      )
  );

  const courses = [...online_courses, ...filtered_classroom_courses].filter(
    (course) => {
      if (course.expiresAt && courseFilters.hideExpired.checked) {
        return moment(course.expiresAt).isAfter(moment());
      }
      return true;
    }
  );

  const formatCourseRow: RowCell<
    NonNullable<
      | CourseTable_delegate$data['myCourses']
      | CourseTable_delegate$data['myClassroomCourses']
    >[number]
  >[] = [
    {
      header: 'Course',
      format: (data) => {
        return <p className={classes.courseName}>{data.course?.name ?? ''}</p>;
      }
    },
    {
      header: 'Category',
      format: (data) => {
        return (
          <p className={classes.allowBreak}>
            {data.course?.category?.name ?? ''}
          </p>
        );
      }
    },
    {
      header: 'Progress',
      format: (data) => {
        if (data.status !== 'incomplete') return <div></div>;
        if (!('progress' in data)) return <div></div>;
        return (
          <CourseCompletion
            total={data.progress?.total ?? 0}
            complete={data.progress?.completed ?? 0}
            width={120}
          />
        );
      }
    },
    {
      header: 'Status',
      format: (data) => {
        return <Status status={data.status} expires={undefined} />;
      }
    },
    {
      header: 'Expiry Date',
      format: (data) => {
        if (('partTwo' in data && data.partTwo?.expiresAt) || data.expiresAt) {
          return moment(
            (('partTwo' in data && data.partTwo?.expiresAt) ||
              data.expiresAt) ??
              ''
          ).format('DD MMMM YYYY');
        }

        return '';
      }
    },
    {
      header: 'Certificate',
      format: (data) => {
        const hasPartTwoCert =
          'partTwo' in data && data.partTwo?.certificateURL;
        const certificateExists = data.certificateURL || hasPartTwoCert;
        return (
          <Button
            archetype="flat"
            disabled={!certificateExists}
            onClick={() => {
              if (data.certificateURL) {
                window.open(data.certificateURL);
                return;
              }

              if ('partTwo' in data && data.partTwo?.certificateURL) {
                window.open(data.partTwo.certificateURL);
                return;
              }
            }}
          >
            {certificateExists ? 'Download' : 'Not Available'}
          </Button>
        );
      }
    }
  ];

  const onCourseFilterClick = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    action({
      type: 'show',
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'bottom-left'
      },
      children: (
        <FilterOptions
          options={courseFilters}
          onChange={(newFilters) => {
            setCourseFilters(newFilters);
          }}
          title={'Course Filters'}
        />
      )
    });
  };

  return (
    <div>
      <h3>Courses</h3>
      <Table<
        NonNullable<
          | CourseTable_delegate$data['myCourses']
          | CourseTable_delegate$data['myClassroomCourses']
        >[number]
      >
        data={courses.sort((a, b) => {
          if (b.enrolledAt < a.enrolledAt) {
            return -1;
          }
          if (a.enrolledAt < b.enrolledAt) {
            return 1;
          }

          return 0;
        })}
        formatRow={formatCourseRow}
        tableName="courses"
        onFilterClick={onCourseFilterClick}
        emptyTableText="No courses yet"
        filterActive={courseFilters.hideExpired.checked}
        fallbackLimit={5}
      />
    </div>
  );
}

const CourseTableFrag = createFragmentContainer(CourseTable, {
  delegate: graphql`
    fragment CourseTable_delegate on Delegate {
      myCourses {
        status
        expiresAt
        enrolledAt
        certificateURL
        progress {
          total
          completed
        }
        partTwo {
          status
          certificateURL
          expiresAt
        }
        course {
          name
          category {
            name
          }
          ... on OnlineCourse {
            partTwo {
              ident: id
            }
          }
        }
      }
      myClassroomCourses {
        status
        expiresAt
        enrolledAt
        certificateURL
        course {
          ident: id
          name
          category {
            name
          }
        }
      }
    }
  `
});

export default CourseTableFrag;
